import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Paper, Grid, Card, CardContent, Divider, Tabs, Tab, useTheme, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button } from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import CategoryIcon from '@mui/icons-material/Category';
import InsightsIcon from '@mui/icons-material/Insights';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SyncIcon from '@mui/icons-material/Sync';
import SpeedIcon from '@mui/icons-material/Speed';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PsychologyIcon from '@mui/icons-material/Psychology';
import BusinessIcon from '@mui/icons-material/Business';
import ClassificationSummary from '../components/DataAnalysis/ClassificationSummary';
import SpendByCategoryChart from '../components/DataAnalysis/SpendByCategoryChart';
import SpendByVendorChart from '../components/DataAnalysis/SpendByVendorChart';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const DataAnalysisPage: React.FC = () => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [mockClassificationData, setMockClassificationData] = useState<any[]>([]);
  const [mockSpendData, setMockSpendData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isTabLoading, setIsTabLoading] = useState(false);
  const [pendingTabIndex, setPendingTabIndex] = useState<number | null>(null);
  const [thinkingDots, setThinkingDots] = useState('');
  const [showSupplierSpend, setShowSupplierSpend] = useState(true);
  const [isProcessingQuery, setIsProcessingQuery] = useState(false);

  // Improved thinking animation effect
  useEffect(() => {
    let dotCount = 0;
    let intervalId: NodeJS.Timeout;
    
    if (isLoading || isTabLoading) {
      // Run the animation faster for better visual feedback
      intervalId = setInterval(() => {
        dotCount = (dotCount + 1) % 4;
        setThinkingDots('.'.repeat(dotCount));
        console.log("Updated thinking dots:", '.'.repeat(dotCount)); // Debug logging
      }, 300);
    }
    
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isLoading, isTabLoading]);

  // Handle tab change with delay
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    // Don't do anything if we're already loading
    if (isTabLoading) return;
    
    // Set loading state and store the requested tab
    setIsTabLoading(true);
    setPendingTabIndex(newValue);
    
    // Wait 3 seconds before changing the tab
    setTimeout(() => {
      setActiveTab(newValue);
      setIsTabLoading(false);
      setPendingTabIndex(null);
    }, 3000);
  };

  // Generate mock classification data based on the 961 rows
  useEffect(() => {
    // Start loading state
    setIsLoading(true);
    
    // Generate mock classification data
    const generateMockClassificationData = () => {
      const categories = [
        'Raw Materials', 
        'Agricultural Goods', 
        'Mining Resources', 
        'Specialty Products', 
        'Energy Resources'
      ];
      
      const subCategories = {
        'Raw Materials': ['Steel', 'Aluminum', 'Copper', 'Plastic', 'Rubber'],
        'Agricultural Goods': ['Grains', 'Dairy', 'Meat', 'Fruits', 'Vegetables'],
        'Mining Resources': ['Iron Ore', 'Coal', 'Lithium', 'Gold', 'Copper Ore'],
        'Specialty Products': ['Chemicals', 'Pharmaceuticals', 'Electronics', 'Textiles', 'Machinery'],
        'Energy Resources': ['Oil', 'Natural Gas', 'Solar Panels', 'Wind Turbines', 'Batteries']
      };
      
      // Simulate 961 items with classification data
      const mockData = Array(961).fill(0).map((_, index) => {
        // Get category with weighted distribution
        const categoryIndex = Math.floor(Math.pow(Math.random(), 2) * categories.length);
        const category = categories[categoryIndex];
        
        // Get subcategory
        const subcategories = subCategories[category as keyof typeof subCategories];
        const subcategory = subcategories[Math.floor(Math.random() * subcategories.length)];
        
        // Generate confidence score with realistic distribution
        // Most items should have high confidence, some medium, few low
        let confidence;
        const rand = Math.random();
        if (rand > 0.85) {
          // Low confidence (0-69%)
          confidence = Math.floor(Math.random() * 30 + 40);
        } else if (rand > 0.5) {
          // Medium confidence (70-89%)
          confidence = Math.floor(Math.random() * 20 + 70);
        } else {
          // High confidence (90-100%)
          confidence = Math.floor(Math.random() * 11 + 90);
        }
        
        // Generate supplier name
        const suppliers = [
          'Kangaroo Commodities Group',
          'Acme Corp',
          'XYZ Industries',
          'Global Supplies Ltd',
          'Regional Distributors'
        ];
        
        // Make Kangaroo Commodities Group have higher spend amounts
        const baseAmount = Math.random() * 5000 + 1000; // Base amount between $1,000 and $6,000
        const supplier = suppliers[index % suppliers.length];
        const amount = supplier === 'Kangaroo Commodities Group'
          ? baseAmount * (Math.random() * 7 + 3) // 3-10x multiplier for Kangaroo
          : baseAmount;
        
        return {
          id: index + 1,
          category,
          subcategory,
          confidence,
          supplier,
          amount,
          timestamp: new Date(2023, Math.floor(index / 80) % 12, (index % 28) + 1),
          processed: true
        };
      });
      
      setMockClassificationData(mockData);
      setMockSpendData(mockData);
    };
    
    generateMockClassificationData();
    
    // Set a 3 second timeout before showing results
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 3000);
    
    // Clear timeout on unmount
    return () => clearTimeout(timer);
  }, []);

  // Calculate classification metrics
  const totalItems = mockClassificationData.length;
  const successfulItems = mockClassificationData.filter(item => item.processed).length;
  const failedItems = totalItems - successfulItems;
  const successRate = totalItems > 0 ? (successfulItems / totalItems) * 100 : 0;
  
  // Calculate average processing time (mock data)
  const avgProcessingTime = 0.5; // seconds per item
  const totalProcessingTime = avgProcessingTime * totalItems;
  
  // Calculate timestamps
  const completedAt = new Date();
  const startedAt = new Date(completedAt.getTime() - (totalProcessingTime * 1000));
  
  // Thinking messages based on what's being loaded
  const getThinkingMessage = (isTabChange: boolean) => {
    const baseMessage = isTabChange 
      ? (pendingTabIndex === 0 ? 'Analyzing classification data' : 'Processing spend analytics') 
      : 'Computing insights from classification run';
      
    // Return the message with the animated dots
    return (
      <Box component="span" display="flex" alignItems="center">
        <span>{baseMessage}</span>
        <Box component="span" sx={{ 
          ml: 0.5, 
          fontWeight: 'bold', 
          fontSize: '1.5em', 
          color: theme.palette.primary.main,
          width: '40px',  // Fixed width for stability
          display: 'inline-block'
        }}>
          {thinkingDots}
        </Box>
      </Box>
    );
  };
  
  // Loading placeholder component for tab changes
  const TabLoadingPlaceholder = () => (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center', 
      minHeight: '300px',
      mt: 4 
    }}>
      <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', mb: 3 }}>
        <PsychologyIcon color="primary" sx={{ fontSize: 40, position: 'absolute', opacity: 0.2 }} />
        <CircularProgress size={60} />
      </Box>
      <Box sx={{ height: '40px', textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          {getThinkingMessage(true)}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Processing data for {totalItems} items
      </Typography>
    </Box>
  );
  
  // Calculate aggregate spend by supplier with some additional helpful information
  const calculateSupplierSpend = () => {
    if (!mockSpendData || mockSpendData.length === 0) return [];
    
    const supplierSpendMap: Record<string, number> = {};
    
    // Aggregate spend by supplier
    mockSpendData.forEach(item => {
      if (item.supplier && item.amount) {
        supplierSpendMap[item.supplier] = (supplierSpendMap[item.supplier] || 0) + item.amount;
      }
    });
    
    // Convert to array and sort by spend (descending)
    const supplierSpendArray = Object.entries(supplierSpendMap).map(([supplier, amount]) => ({
      supplier,
      amount,
      percentage: (amount / totalSpend) * 100
    }));
    
    return supplierSpendArray.sort((a, b) => b.amount - a.amount);
  };
  
  // Calculate total spend
  const totalSpend = mockSpendData.reduce((sum, item) => sum + (item.amount || 0), 0);
  
  // Supplier spend table component with improved formatting
  const SupplierSpendTable = () => {
    const supplierSpendData = calculateSupplierSpend();
    
    return (
      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Typography variant="h6" sx={{ p: 2, bgcolor: theme.palette.primary.main, color: 'white' }}>
          Aggregate Spend for Each Supplier
        </Typography>
        <Table>
          <TableHead sx={{ backgroundColor: theme.palette.grey[100] }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Supplier</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total Spend</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Percentage</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {supplierSpendData.map((row) => (
              <TableRow 
                key={row.supplier} 
                sx={{ 
                  '&:last-child td, &:last-child th': { border: 0 },
                  bgcolor: row.supplier === 'Kangaroo Commodities Group' ? theme.palette.primary.light + '20' : 'inherit'
                }}
              >
                <TableCell component="th" scope="row" sx={{ fontWeight: row.supplier === 'Kangaroo Commodities Group' ? 'bold' : 'normal' }}>
                  {row.supplier}
                </TableCell>
                <TableCell align="right">${row.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                <TableCell align="right">{row.percentage.toFixed(2)}%</TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ backgroundColor: theme.palette.grey[200] }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>${totalSpend.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>100.00%</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  };
  
  // Query processing placeholder
  const QueryProcessingPlaceholder = () => (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center', 
      minHeight: '200px',
      mt: 4,
      mb: 2 
    }}>
      <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', mb: 3 }}>
        <BusinessIcon color="primary" sx={{ fontSize: 40, position: 'absolute', opacity: 0.2 }} />
        <CircularProgress size={60} />
      </Box>
      <Box sx={{ height: '40px', textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          <Box component="span" display="flex" alignItems="center">
            <span>Calculating supplier spend aggregates</span>
            <Box component="span" sx={{ 
              ml: 0.5, 
              fontWeight: 'bold', 
              fontSize: '1.5em', 
              color: theme.palette.primary.main,
              width: '40px',
              display: 'inline-block'
            }}>
              {thinkingDots}
            </Box>
          </Box>
        </Typography>
      </Box>
    </Box>
  );
  
  // Main content component
  const MainContent = () => (
    <>
      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Total Items */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <AssessmentIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" component="div">
                  Total Items
                </Typography>
              </Box>
              <Typography variant="h3" color="text.primary" fontWeight="bold">
                {totalItems}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total items processed in this run
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Success Rate */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="h6" component="div">
                  Success Rate
                </Typography>
              </Box>
              <Typography variant="h3" color="text.primary" fontWeight="bold">
                {successRate.toFixed(1)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {successfulItems} successful, {failedItems} failed
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Processing Time */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <AccessTimeIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" component="div">
                  Processing Time
                </Typography>
              </Box>
              <Typography variant="h3" color="text.primary" fontWeight="bold">
                {totalProcessingTime.toFixed(2)}s
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {avgProcessingTime.toFixed(3)}s per item
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Run Timestamp */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <SyncIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" component="div">
                  Run Timestamp
                </Typography>
              </Box>
              <Typography variant="h3" color="text.primary" fontWeight="bold">
                {completedAt.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Started: {startedAt.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Supplier Spend Analysis - Now always visible */}
      <Box sx={{ mb: 4 }}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <BusinessIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6" component="div">
                Supplier Spend Analysis
              </Typography>
            </Box>
            
            <Typography variant="body1" sx={{ mb: 3 }}>
              Below is the aggregate spend for each supplier based on the processed data.
              This analysis shows the total amount spent with each supplier and what percentage of total spend they represent.
            </Typography>
            
            <SupplierSpendTable />
          </CardContent>
        </Card>
      </Box>
      
      {/* Tabs Section */}
      <Box sx={{ width: '100%', mb: 4 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={isTabLoading ? (pendingTabIndex === null ? activeTab : pendingTabIndex) : activeTab}
            onChange={handleTabChange} 
            aria-label="classification tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab 
              label="Classification Summary" 
              icon={<CategoryIcon />} 
              iconPosition="start" 
              {...a11yProps(0)} 
              disabled={isTabLoading}
            />
            <Tab 
              label="Spend Analysis" 
              icon={<BarChartIcon />} 
              iconPosition="start" 
              {...a11yProps(1)} 
              disabled={isTabLoading}
            />
          </Tabs>
        </Box>
        
        {isTabLoading ? (
          <TabLoadingPlaceholder />
        ) : (
          <>
            {/* Classification Summary Tab */}
            <TabPanel value={activeTab} index={0}>
              <ClassificationSummary data={mockClassificationData} />
            </TabPanel>
            
            {/* Spend Analysis Tab */}
            <TabPanel value={activeTab} index={1}>
              <Grid container spacing={4}>
                {/* Spend by Category */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Spend by Category
                  </Typography>
                  <SpendByCategoryChart data={mockSpendData} />
                </Grid>
                
                {/* Spend by Vendor */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Spend by Vendor
                  </Typography>
                  <SpendByVendorChart data={mockSpendData} />
                </Grid>
              </Grid>
            </TabPanel>
          </>
        )}
      </Box>
    </>
  );
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {isLoading ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '50vh',
          mt: 4 
        }}>
          <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center', mb: 3 }}>
            <PsychologyIcon color="primary" sx={{ fontSize: 50, position: 'absolute', opacity: 0.2 }} />
            <CircularProgress size={70} />
          </Box>
          <Box sx={{ height: '50px', textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Typography variant="h5" color="text.secondary">
              {getThinkingMessage(false)}
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            Processing data for {totalItems} items
          </Typography>
        </Box>
      ) : <MainContent />}
    </Container>
  );
};

export default DataAnalysisPage; 